import { Chip } from "primereact/chip";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import styled from "styled-components";

import {
  payerCountLabel,
  providerCountLabel,
} from "../../PayerRulesPage/helpers/ruleBucket";

const MAX_CHIPPED_PAYERS = 3;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
`;

const PayerChip = styled(Chip)`
  &.p-chip {
    font-size: 0.75rem;
    padding: 0 0.5rem;
    border-radius: 8px;
    background: var(--surface-100);
    color: var(--text-color);
  }
  .p-chip-text {
    margin: 0.15rem 0;
    line-height: 1.2;
  }
`;

const CountTag = styled(Tag)`
  &.p-tag.p-component {
    background: var(--primary-50) !important;
    color: var(--primary-700) !important;
    border: 1px solid var(--primary-200) !important;
    font-weight: 600;
    font-size: 0.7rem;
    border-radius: 8px;
  }
`;

/** A DOM-safe class name, unique per row and kind, for a tooltip target. */
const targetClass = (kind: string, rowId: string) =>
  `bucket-${kind}-${rowId.replace(/[^a-zA-Z0-9_-]/g, "")}`;

interface ICountTagCellProps {
  /** The display row's id — gives the tooltip a unique target. */
  rowId: string;
  /** "providers", "payers", "names" — part of the tooltip target. */
  kind: string;
  label: string;
  names: string[];
}

/**
 * CLE-2842 — a count tag ("18 providers", "27 payers", "3 names") whose tooltip lists every name.
 */
export function CountTagWithTooltip({
  rowId,
  kind,
  label,
  names,
}: ICountTagCellProps) {
  const className = targetClass(kind, rowId);
  return (
    <>
      <CountTag
        className={className}
        value={label}
        data-pr-tooltip={names.join(", ")}
        data-pr-position="top"
      />
      <Tooltip target={`.${className}`} />
    </>
  );
}

interface IPayerChipsCellProps {
  /** The display row's id — used to give this cell's tooltip a unique target. */
  rowId: string;
  /** Distinct payer names of the bucket. */
  payerNames: string[];
}

/**
 * CLE-2842 — the Payer cell of a bucket row (one rule copied to several payers): up to three
 * payers as chips next to the payer count; beyond that, only the count, with every payer in its
 * tooltip.
 */
export function PayerChipsCell({ rowId, payerNames }: IPayerChipsCellProps) {
  const label = payerCountLabel(payerNames.length);
  if (payerNames.length > MAX_CHIPPED_PAYERS) {
    return (
      <Wrapper>
        <CountTagWithTooltip
          rowId={rowId}
          kind="payers"
          label={label}
          names={payerNames}
        />
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <CountTag value={label} />
      {payerNames.map((name, index) => (
        // Two payers can share a display name, so the index keeps the keys unique.
        // eslint-disable-next-line react/no-array-index-key
        <PayerChip key={`${name}-${index}`} label={name} />
      ))}
    </Wrapper>
  );
}

/** CLE-2842 — the Provider cell of a bucket spanning several providers. */
export function ProviderCountCell({
  rowId,
  providerNames,
}: {
  rowId: string;
  providerNames: string[];
}) {
  return (
    <Wrapper>
      <CountTagWithTooltip
        rowId={rowId}
        kind="providers"
        label={providerCountLabel(providerNames.length)}
        names={providerNames}
      />
    </Wrapper>
  );
}
